import { useCallback, useEffect, useState } from 'react';
import { formatCents, type PricingConfig } from '@lbhl/shared';
import {
  createDraft,
  fetchDraftDiff,
  fetchPricingVersions,
  fetchReviewQueue,
  patchDraft,
  patchDraftInventory,
  fetchPricingVersionConfig,
  publishDraftVersion,
  addCostItem,
  removeCostItem,
  discardDraft,
  type CostDriver,
  type NewCostItemBody,
  type DraftPatchBody,
  type PricingVersionRow,
  type RateChange,
  type ReviewRow,
} from '../lib/api.js';

/**
 * The pricing admin.
 *
 * There is deliberately no "edit the live rate" control, because published
 * versions are frozen by database triggers. Changing a rate means cloning the
 * published version into a draft, editing that, reviewing the diff, and
 * publishing — which is what keeps every bid ever quoted reproducible.
 */
export function Admin({ onPublished }: { onPublished(): void }) {
  const [versions, setVersions] = useState<PricingVersionRow[]>([]);
  const [draft, setDraft] = useState<PricingVersionRow | null>(null);
  const [diff, setDiff] = useState<RateChange[] | null>(null);
  const [draftConfig, setDraftConfig] = useState<PricingConfig | null>(null);
  const [review, setReview] = useState<ReviewRow[]>([]);
  const [reviewCounts, setReviewCounts] = useState({ needs_review: 0, quoted_on_superseded: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [rows, queue] = await Promise.all([fetchPricingVersions(), fetchReviewQueue()]);
      setVersions(rows);
      setDraft(rows.find((v) => v.status === 'draft') ?? null);
      setReview(queue.bids);
      setReviewCounts(queue.counts);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => void reload(), [reload]);

  // Refetched whenever the draft changes, which `reload()` triggers after every
  // patch -- so the diff on screen always reflects the last saved edit.
  useEffect(() => {
    if (!draft) {
      setDiff(null);
      setDraftConfig(null);
      return;
    }
    let cancelled = false;
    // Cleared first: leaving the previous diff on screen during the refetch would
    // keep Publish enabled against a diff that is already out of date.
    setDiff(null);
    void Promise.all([fetchDraftDiff(draft.id), fetchPricingVersionConfig(draft.id)])
      .then(([d, config]) => {
        if (cancelled) return;
        setDiff(d.changes);
        setDraftConfig(config);
      })
      .catch(() => {
        if (!cancelled) setDiff(null);
      });
    return () => {
      cancelled = true;
    };
  }, [draft]);

  async function run(label: string, fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const published = versions.find((v) => v.status === 'published');

  return (
    <>
      {error && <div className="note bad">{error}</div>}
      {notice && <div className="note">{notice}</div>}

      <section className="card">
        <h2>Live pricing</h2>
        {published ? (
          <table className="lines">
            <tbody>
              <tr>
                <td>Rate per foot</td>
                <td className="num">{formatCents(published.rate_per_foot_cents)}</td>
              </tr>
              <tr>
                <td>Accent strand</td>
                <td className="num">{formatCents(published.price_per_strand_cents)}</td>
              </tr>
              <tr>
                <td>
                  Travel per mile
                  {published.travel_rate_cents_per_mile === 0 && (
                    <>
                      <br />
                      <span className="unit" style={{ fontSize: 12, opacity: 0.75 }}>
                        not set — travel currently adds nothing to a bid
                      </span>
                    </>
                  )}
                </td>
                <td className="num">{formatCents(published.travel_rate_cents_per_mile)}</td>
              </tr>
              <tr>
                <td>Travel free radius</td>
                <td className="num">{Number(published.travel_free_radius_miles)} mi</td>
              </tr>
              <tr className="total">
                <td>Basis</td>
                <td className="num">
                  {published.travel_basis === 'round_trip' ? 'Round trip' : 'One way'}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className="note bad">Nothing is published.</div>
        )}
        <div className="note">
          Published rates cannot be edited — the database refuses it, so that a bid quoted with last
          month's prices still stands. To change a rate, draft a new version below.
        </div>
      </section>

      {draft ? (
        <DraftEditor
          // Keyed by id so switching drafts remounts with that draft's values.
          key={draft.id}
          draft={draft}
          draftConfig={draftConfig}
          diff={diff}
          busy={busy}
          onPatch={(patch) => run('Draft updated.', () => patchDraft(draft.id, patch))}
          onPatchCost={(sku, cents) =>
            run('Cost updated.', () => patchDraftInventory(draft.id, sku, cents))
          }
          onAddCost={(item) =>
            run(`Added "${item.name}" to the cost model.`, async () => {
              await addCostItem(draft.id, item);
            })
          }
          onRemoveCost={(sku, name) =>
            run(`Removed "${name}" from the cost model.`, () => removeCostItem(draft.id, sku))
          }
          onPublish={() =>
            run('Published. Devices will pick it up on their next sync.', async () => {
              await publishDraftVersion(draft.id);
              onPublished();
            })
          }
          onDiscard={() =>
            run('Draft discarded. Live pricing is unchanged.', async () => {
              await discardDraft(draft.id);
            })
          }
        />
      ) : (
        <section className="card">
          <h2>New pricing version</h2>
          <div className="note">
            This copies the live version into an editable draft. Nothing changes for estimators
            until you publish it.
          </div>
          <button
            className="primary"
            disabled={busy || !published}
            onClick={() =>
              run('Draft created from the live version.', async () => {
                await createDraft(`Draft from ${new Date().toLocaleDateString()}`);
              })
            }
          >
            Draft a new version
          </button>
        </section>
      )}

      <section className="card">
        <h2>
          Needs a look — {reviewCounts.needs_review} flagged,{' '}
          {reviewCounts.quoted_on_superseded} quoted offline on old pricing
        </h2>
        {review.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>
            Nothing flagged. Everything checks out.
          </div>
        ) : (
          review.map((r) => (
            <div className="bidrow" key={r.id}>
              <span className="who">
                <b>
                  {formatCents(r.total_cents)}
                  {r.selected_package !== null ? ` · ${r.selected_package}` : ''}
                </b>
                <span>
                  {r.created_by_email} · {new Date(r.created_at).toLocaleDateString()} · engine{' '}
                  {r.engine_version}
                </span>
                {r.needs_review && <span className="badge bad">{r.review_note}</span>}
                {r.quoted_on_superseded && (
                  <span className="badge warn">
                    Quoted offline on {r.pricing_label}, which was no longer live at sync —
                    honored as quoted
                  </span>
                )}
              </span>
            </div>
          ))
        )}
      </section>

      <section className="card">
        <h2>Version history</h2>
        {versions.map((v) => (
          <div className="bidrow" key={v.id}>
            <span className="who">
              <b>
                #{v.id} {v.label}
              </b>
              <span>
                {formatCents(v.rate_per_foot_cents)}/ft ·{' '}
                {v.published_at
                  ? `published ${new Date(v.published_at).toLocaleDateString()}`
                  : `created ${new Date(v.created_at).toLocaleDateString()}`}
              </span>
            </span>
            <span className={`badge ${v.status === 'published' ? 'ok' : ''}`}>{v.status}</span>
          </div>
        ))}
      </section>
    </>
  );
}

function DraftEditor({
  draft,
  draftConfig,
  diff,
  busy,
  onPatch,
  onPatchCost,
  onAddCost,
  onRemoveCost,
  onPublish,
  onDiscard,
}: {
  draft: PricingVersionRow;
  /** The DRAFT's own config, so edited material costs show their edited values. */
  draftConfig: PricingConfig | null;
  diff: RateChange[] | null;
  busy: boolean;
  onPatch(patch: DraftPatchBody): Promise<void>;
  onPatchCost(sku: string, unitCostCents: number): Promise<void>;
  onAddCost(item: NewCostItemBody): Promise<void>;
  onRemoveCost(sku: string, name: string): Promise<void>;
  onPublish(): void;
  onDiscard(): void;
}) {
  // Dollars as strings while typing; converted to integer cents on commit so no
  // float ever reaches the database.
  const [rate, setRate] = useState((draft.rate_per_foot_cents / 100).toFixed(2));
  const [strand, setStrand] = useState((draft.price_per_strand_cents / 100).toFixed(2));
  const [travel, setTravel] = useState((draft.travel_rate_cents_per_mile / 100).toFixed(2));
  const [radius, setRadius] = useState(String(Number(draft.travel_free_radius_miles)));
  const [basis, setBasis] = useState(draft.travel_basis);
  /**
   * How many edits are still in flight.
   *
   * A field commits on blur, and tapping Publish blurs it — so without this the
   * patch and the publish race, and the draft can go live without the edit the
   * admin just typed, or the patch can land on a now-frozen version and error.
   * Either way the diff they reviewed would not be what shipped.
   */
  const [inFlight, setInFlight] = useState(0);
  const [discarding, setDiscarding] = useState(false);

  /** Commits a field, ignoring a blank or non-numeric entry. */
  async function commit(raw: string, build: (n: number) => DraftPatchBody) {
    const trimmed = raw.trim();
    // Number('') is 0, so a cleared field would otherwise silently set $0.00.
    if (trimmed === '' || !Number.isFinite(Number(trimmed))) return;
    setInFlight((n) => n + 1);
    try {
      await onPatch(build(Number(trimmed)));
    } finally {
      setInFlight((n) => n - 1);
    }
  }

  const dollarsToCents = (v: number) => Math.round(v * 100);

  return (
    <>
      <section className="card">
        <h2>Draft #{draft.id} — {draft.label}</h2>

        <div className="field">
          <label>Rate per foot ($/ft) — shared by all three packages</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => void commit(rate, (n) => ({ ratePerFootCents: dollarsToCents(n) }))}
          />
        </div>

        <div className="field">
          <label>Accent mini-light strand ($ each)</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={strand}
            onChange={(e) => setStrand(e.target.value)}
            onBlur={() => void commit(strand, (n) => ({ pricePerStrandCents: dollarsToCents(n) }))}
          />
        </div>

        <div className="field">
          <label>Travel ($ per mile)</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={travel}
            onChange={(e) => setTravel(e.target.value)}
            onBlur={() => void commit(travel, (n) => ({ travelRateCentsPerMile: dollarsToCents(n) }))}
          />
        </div>

        <div className="field">
          <label>Free radius (miles) — travel is only billed beyond this</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            onBlur={() => void commit(radius, (n) => ({ travelFreeRadiusMiles: n }))}
          />
        </div>

        <div className="field">
          <label>Travel basis</label>
          <div className="seg">
            {(['one_way', 'round_trip'] as const).map((b) => (
              <button
                key={b}
                type="button"
                aria-pressed={basis === b}
                onClick={() => {
                  setBasis(b);
                  setInFlight((n) => n + 1);
                  void onPatch({ travelBasis: b }).finally(() => setInFlight((n) => n - 1));
                }}
              >
                {b === 'one_way' ? 'One way' : 'Round trip'}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="card">
        <h2>Cost inputs — internal only, never billed</h2>
        <div className="note">
          These never change what a customer pays. They feed the margin figure, which excludes
          labor. Not limited to materials: a flat per-bid cost such as customer acquisition or a
          permit fee belongs here too.
        </div>
        {draftConfig === null ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Loading draft costs…</div>
        ) : (
          draftConfig.inventoryItems.map((item) => (
            <InventoryCostRow
              key={item.sku}
              name={item.name}
              uom={item.uom}
              cents={item.unitCostCents}
              driver={draftConfig.bomRules.find((r) => r.sku === item.sku)?.driver}
              busy={busy}
              onCommit={async (cents) => {
                setInFlight((n) => n + 1);
                try {
                  await onPatchCost(item.sku, cents);
                } finally {
                  setInFlight((n) => n - 1);
                }
              }}
              onRemove={() => void onRemoveCost(item.sku, item.name)}
            />
          ))
        )}
      </section>

      <AddCostForm busy={busy} onAdd={onAddCost} />

      <section className="card">
        <h2>Review before publishing</h2>
        {diff === null ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Loading diff…</div>
        ) : diff.length === 0 ? (
          <div className="note">
            This draft is identical to the live version. Publishing it would change nothing.
          </div>
        ) : (
          <table className="lines">
            <tbody>
              {diff.map((c) => (
                <tr key={c.field}>
                  <td>{c.label}</td>
                  <td className="num dim">{c.from}</td>
                  <td className="num">→ {c.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button
          className="primary"
          style={{ marginTop: 14 }}
          disabled={busy || inFlight > 0 || diff === null || diff.length === 0}
          onClick={onPublish}
        >
          {inFlight > 0 ? 'Saving edits…' : `Publish draft #${draft.id}`}
        </button>
        {discarding ? (
          <div className="note warn" style={{ marginTop: 12 }}>
            Discard draft #{draft.id} and lose these edits? Live pricing is untouched either way.
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button className="primary" disabled={busy} onClick={onDiscard}>
                Discard it
              </button>
              <button className="ghost" onClick={() => setDiscarding(false)}>
                Keep editing
              </button>
            </div>
          </div>
        ) : (
          <button className="ghost" style={{ marginTop: 10 }} onClick={() => setDiscarding(true)}>
            Discard this draft
          </button>
        )}

        <div className="note" style={{ marginTop: 12 }}>
          Publishing archives the current version. Bids already quoted keep their old pricing
           — they are never repriced. Only one draft exists at a time, so
          publish or discard this one before starting another.
        </div>
      </section>
    </>
  );
}

const DRIVER_LABELS: Record<CostDriver, string> = {
  per_measured_foot: 'per measured foot',
  per_strand: 'per accent strand',
  per_run: 'per run',
  per_bid: 'once per bid',
  // The quantity is not derived at all -- a bidder types it on the bid's
  // Engineering section, for parts no measurement implies.
  manual_entry: 'counted on the bid',
};

function InventoryCostRow({
  name,
  uom,
  cents,
  driver,
  busy,
  onCommit,
  onRemove,
}: {
  name: string;
  uom: string;
  cents: number;
  driver?: string | undefined;
  busy: boolean;
  onCommit(cents: number): Promise<void>;
  onRemove(): void;
}) {
  const [value, setValue] = useState((cents / 100).toFixed(2));
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="costrow">
      <span className="who">
        <b>{name}</b>
        <span>
          $ per {uom}
          {driver
            ? ` · counted ${DRIVER_LABELS[driver as CostDriver] ?? driver}`
            : ' · no rule, so it does not affect margin'}
        </span>
      </span>
      <input
        aria-label={`${name} unit cost`}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const trimmed = value.trim();
          if (trimmed === '' || !Number.isFinite(Number(trimmed))) return;
          const next = Math.round(Number(trimmed) * 100);
          if (next !== cents) void onCommit(next);
        }}
      />
      {confirming ? (
        <>
          <button className="primary" disabled={busy} onClick={onRemove}>
            Remove
          </button>
          <button className="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button
          className="ghost"
          aria-label={`Remove ${name}`}
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * Adds a cost input.
 *
 * The driver is part of the form rather than a later step, because an item with no
 * rule contributes nothing to the margin — it would look added but do nothing.
 */
function AddCostForm({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd(item: NewCostItemBody): Promise<void>;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [dollars, setDollars] = useState('');
  const [driver, setDriver] = useState<CostDriver>('per_bid');

  const amount = Number(dollars);
  const canSubmit =
    name.trim() !== '' && category.trim() !== '' && dollars.trim() !== '' &&
    Number.isFinite(amount) && amount >= 0 && !busy;

  // A per-foot or per-strand cost is quantified in feet/strands; a flat cost is a
  // single unit. Deriving this removes a field nobody would enjoy reasoning about.
  const uom: 'foot' | 'unit' = driver === 'per_measured_foot' ? 'foot' : 'unit';

  async function submit() {
    if (!canSubmit) return;
    await onAdd({
      name: name.trim(),
      category: category.trim(),
      uom,
      unitCostCents: Math.round(amount * 100),
      driver,
      qtyPerDriver: 1,
    });
    setName('');
    setCategory('');
    setDollars('');
    setDriver('per_bid');
  }

  return (
    <section className="card">
      <h2>Add a cost input</h2>
      <div className="addform">
        <div className="pair">
          <div className="field" style={{ marginBottom: 0 }}>
            <label>What is it</label>
            <input
              placeholder="e.g. Customer acquisition"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Category</label>
            <input
              placeholder="e.g. Marketing"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <label>How is it counted</label>
          <div className="seg">
            {(Object.keys(DRIVER_LABELS) as CostDriver[]).map((d) => (
              <button key={d} type="button" aria-pressed={driver === d} onClick={() => setDriver(d)}>
                {DRIVER_LABELS[d]}
              </button>
            ))}
          </div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <label>
            Cost in dollars{' '}
            {driver === 'per_measured_foot'
              ? 'per foot'
              : driver === 'per_strand'
                ? 'per strand'
                : driver === 'per_run'
                  ? 'per run'
                  : driver === 'manual_entry'
                    ? 'per unit counted'
                    : 'per bid'}
          </label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void submit()}
          />
        </div>

        <button className="primary" disabled={!canSubmit} onClick={() => void submit()}>
          Add cost input
        </button>
      </div>
    </section>
  );
}
